import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Select,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Article,
  ArticleUser,
  UpdateArticleUserRequest,
  articlesApi,
} from '@texas/api/endpoints/articlesApi';
import { customerUsersApi } from '@texas/api/endpoints/customerUsersApi';
import { UserType } from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { getUserTypeLabel } from '@texas/components/customer/users/shared';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { Icons } from '@texas/components/shared/Icons';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { userTypeFriendlyName } from '@texas/utils/helpers/roleHelpers';
import { TFunction } from 'i18next';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleUserModal } from './ArticleUserModal';
import { AddUser } from '@texas/components/customer/users/AddUser';
import { UserAvatar } from '@texas/components/shared/users/UserAvatar';
import {
  TeamMatrixBranchHeader,
  TeamMatrixCell,
  TeamMatrixCorner,
  TeamMatrixGrid,
  TeamMatrixRoleHeader,
} from '@texas/components/shared/users/TeamMatrix';

interface AssignmentPreset {
  branchId: number | null;
  userType: UserType | null;
}

interface RoleRow {
  key: string;
  label: string;
  description: string;
  userType: UserType | null;
}

type AssignmentFilter = 'all' | 'article' | 'inherited' | 'missing';

export function PeopleManagement({ article }: { article: Article }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: isRemoveOpen,
    onClose: onRemoveClose,
    onOpen: onRemoveOpen,
  } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState<ArticleUser | null>(null);
  const [assignmentPreset, setAssignmentPreset] =
    useState<AssignmentPreset | null>(null);
  const [search, setSearch] = useState('');
  const [assignmentFilter, setAssignmentFilter] =
    useState<AssignmentFilter>('all');
  const {
    data: articleUsers,
    loading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useApiResource(articlesApi.getArticleUsers);
  const {
    data: articleBranches,
    loading: branchesLoading,
    error: branchesError,
    refetch: refetchBranches,
  } = useApiResource(articlesApi.getArticleBranches);
  const { request: addUser, loading: adding } = useApiRequest(
    articlesApi.addArticleUser,
  );
  const { request: updateUser, loading: updating } = useApiRequest(
    articlesApi.updateArticleUser,
  );
  const { request: addCustomerUser } = useApiRequest(customerUsersApi.addUser);
  const { request: setCustomerCustomType } = useApiRequest(
    customerUsersApi.setCustomType,
  );

  useEffect(() => {
    refetchUsers(article.id);
    refetchBranches(article.id);
  }, [article.id, refetchBranches, refetchUsers]);

  const users = useMemo<ArticleUser[]>(
    () => articleUsers ?? [],
    [articleUsers],
  );
  const branches = useMemo(() => articleBranches ?? [], [articleBranches]);
  const roleRows = useMemo<RoleRow[]>(
    () => [
      {
        key: 'product-developer',
        label: userTypeFriendlyName(UserType.ProductDeveloper, t),
        description: t('user.userType.short.productDeveloper'),
        userType: UserType.ProductDeveloper,
      },
      {
        key: 'customer-service',
        label: userTypeFriendlyName(UserType.CustomerService, t),
        description: t('user.userType.short.customerService'),
        userType: UserType.CustomerService,
      },
      {
        key: 'other',
        label: t('article.management.otherRoles'),
        description: t('article.management.additionalArticleAccess'),
        userType: null,
      },
    ],
    [t],
  );
  const filteredUsers = users.filter((user) => {
    const normalizedSearch = search.trim().toLowerCase();
    const matchesSearch =
      normalizedSearch.length === 0 ||
      `${user.name} ${user.email}`.toLowerCase().includes(normalizedSearch);
    const matchesAssignment =
      assignmentFilter === 'all' ||
      (assignmentFilter === 'inherited' && user.inherited) ||
      (assignmentFilter === 'article' && !user.inherited) ||
      (assignmentFilter === 'missing' && !user.inCustomerTeam);

    return matchesSearch && matchesAssignment;
  });
  const peopleCount = new Set(users.map((user) => user.userId)).size;
  const isFiltered = search.trim().length > 0 || assignmentFilter !== 'all';

  const openAddPerson = (preset: AssignmentPreset | null = null) => {
    setSelectedUser(null);
    setAssignmentPreset(preset);
    onOpen();
  };
  const openEditPerson = (user: ArticleUser) => {
    setAssignmentPreset(null);
    setSelectedUser(user);
    onOpen();
  };
  const confirmRemovePerson = () => {
    onClose();
    onRemoveOpen();
  };
  const openRemovePerson = (user: ArticleUser) => {
    setSelectedUser(user);
    onRemoveOpen();
  };
  const addPersonToCustomerTeam = async (user: ArticleUser) => {
    const userType = user.userType
      ? ensureEnumNumber(UserType, user.userType)
      : null;
    const response = await addCustomerUser(article.customerId, {
      userId: user.userId,
      branchId: user.branchId ?? undefined,
      type: userType,
    });

    if (response.hasError) {
      toast({
        title: t('general.actionFailed'),
        description: response.error.message,
        status: 'error',
        isClosable: true,
      });
      return;
    }

    if (user.customUserType) {
      const customTypeResponse = await setCustomerCustomType(response.data, {
        customType: user.customUserType,
      });
      if (customTypeResponse.hasError) {
        toast({
          title: t('general.actionFailed'),
          description: customTypeResponse.error.message,
          status: 'error',
          isClosable: true,
        });
        void refetchUsers(article.id);
        return;
      }
    }

    toast({
      title: t('article.management.personAddedToCustomerTeam'),
      status: 'success',
      isClosable: true,
    });
    void refetchUsers(article.id);
  };
  const savePerson = async (
    userId: number,
    assignment: UpdateArticleUserRequest,
  ) => {
    const response = selectedUser
      ? await updateUser(article.id, selectedUser.id, assignment)
      : await addUser(article.id, { userId, ...assignment });

    if (response.hasError) {
      toast({
        title: t('general.actionFailed'),
        description: response.error.message,
        status: 'error',
        isClosable: true,
      });
      return;
    }

    toast({
      title: t('article.management.personSaved'),
      status: 'success',
      isClosable: true,
    });
    onClose();
    void refetchUsers(article.id);
  };

  return (
    <>
      <Flex justify="flex-end" mb={3}>
        <Button variant="texas-solid" onClick={() => openAddPerson()}>
          {t('article.management.addPerson')}
        </Button>
      </Flex>

      <Card variant="dark">
        <CardBody p={4}>
          <Flex
            align={{ base: 'stretch', lg: 'flex-start' }}
            direction={{ base: 'column', lg: 'row' }}
            gap={3}
            justify="space-between"
            mb={4}
          >
            <Box>
              <Heading fontFamily="barlow" fontSize="22px" lineHeight="27px">
                {t('article.management.articleTeamByBranch')}
              </Heading>
              <Text color="articleManagement.mutedText" fontSize="sm" mt={1}>
                {t('article.management.teamByBranchDescription')}
              </Text>
            </Box>
            <Flex gap={2} wrap="wrap">
              <Input
                h="34px"
                maxW="180px"
                placeholder={t('article.management.searchPeople')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <Select
                h="34px"
                maxW="180px"
                value={assignmentFilter}
                onChange={(event) =>
                  setAssignmentFilter(event.target.value as AssignmentFilter)
                }
              >
                <option value="all">
                  {t('article.management.allAssignments')}
                </option>
                <option value="inherited">
                  {t('article.management.inherited')}
                </option>
                <option value="article">
                  {t('article.management.articleSpecific')}
                </option>
                <option value="missing">
                  {t('article.management.notInCustomerTeam')}
                </option>
              </Select>
            </Flex>
          </Flex>

          {(usersLoading || branchesLoading) && (
            <Flex align="center" justify="center" minH="180px">
              <Spinner />
            </Flex>
          )}
          {usersError && <ErrorDetails error={usersError} />}
          {branchesError && <ErrorDetails error={branchesError} />}

          {!usersLoading && !branchesLoading && !usersError && !branchesError && (
            <>
              <TeamMatrixGrid branchCount={branches.length}>
                <TeamMatrixCorner />
                {branches.map((branch) => (
                  <TeamMatrixBranchHeader key={branch.id} branch={branch} />
                ))}
                {roleRows.map((roleRow, rowIndex) => (
                  <Fragment key={roleRow.key}>
                    <TeamMatrixRoleHeader
                      description={roleRow.description}
                      rowIndex={rowIndex + 2}
                      title={roleRow.label}
                    />
                    {branches.map((branch, columnIndex) => {
                      const cellUsers = filteredUsers.filter(
                        (user) =>
                          user.branchId === branch.id &&
                          userMatchesRole(user, roleRow.userType),
                      );

                      return (
                        <TeamMatrixCell
                          key={`${roleRow.key}-${branch.id}`}
                          columnIndex={columnIndex + 2}
                          rowIndex={rowIndex + 2}
                        >
                          <Flex direction="column" gap={3}>
                            {cellUsers.map((user) => (
                              <AssignmentCard
                                key={user.id}
                                user={user}
                                showRole={roleRow.userType === null}
                                onAddToCustomerTeam={addPersonToCustomerTeam}
                                onEdit={openEditPerson}
                                onRemove={openRemovePerson}
                              />
                            ))}
                            {!isFiltered && (
                              <AddUser
                                onClick={() =>
                                  openAddPerson({
                                    branchId: branch.id,
                                    userType: roleRow.userType,
                                  })
                                }
                                size="sm"
                                sub={t('general.clickToAdd')}
                                title={t('customerUser.addUser')}
                              />
                            )}
                          </Flex>
                        </TeamMatrixCell>
                      );
                    })}
                  </Fragment>
                ))}
              </TeamMatrixGrid>

              <Flex align="center" justify="space-between" mt={3}>
                <Flex align="center" gap={2}>
                  <Box bg="status.success" borderRadius="full" boxSize="7px" />
                  <Text color="articleManagement.mutedText" fontSize="xs">
                    {t('article.management.assignmentsSaved')}
                  </Text>
                </Flex>
                <Text color="articleManagement.mutedText" fontSize="xs">
                  {t('article.management.peopleAndBranches', {
                    people: peopleCount,
                    branches: branches.length,
                  })}
                </Text>
              </Flex>
            </>
          )}
        </CardBody>
      </Card>

      <ArticleUserModal
        article={article}
        articleUser={selectedUser}
        articleUsers={users}
        initialAssignment={assignmentPreset}
        isOpen={isOpen}
        loading={adding || updating}
        onClose={onClose}
        onRemove={confirmRemovePerson}
        onSave={(userId, assignment) => void savePerson(userId, assignment)}
      />
      {selectedUser && (
        <VerifyDialogWithRequest
          args={[article.id, selectedUser.id]}
          headerTitle={t('article.management.removePersonTitle', {
            name: selectedUser.name,
          })}
          isOpen={isRemoveOpen}
          onClose={onRemoveClose}
          onPerformed={() => {
            setSelectedUser(null);
            void refetchUsers(article.id);
          }}
          onSuccessTitle={t('article.management.personRemoved')}
          primaryButtonTitle={t('article.management.removePerson')}
          primaryRequest={articlesApi.removeArticleUser}
          secondaryButtonTitle={t('general.cancel')}
        >
          {t('article.management.removePersonDescription')}
        </VerifyDialogWithRequest>
      )}
    </>
  );
}

function AssignmentCard({
  user,
  showRole,
  onAddToCustomerTeam,
  onEdit,
  onRemove,
}: {
  user: ArticleUser;
  showRole: boolean;
  onAddToCustomerTeam: (user: ArticleUser) => void;
  onEdit: (user: ArticleUser) => void;
  onRemove: (user: ArticleUser) => void;
}) {
  const { t } = useTranslation();
  const assignmentLabel = t(
    user.inherited
      ? 'article.management.inherited'
      : 'article.management.articleSpecific',
  );
  const secondaryLabel = showRole
    ? `${roleName(user, t)} · ${assignmentLabel}`
    : assignmentLabel;

  return (
    <Flex direction="column" gap={1}>
      <UserAvatar
        buttons={
          <Menu placement="bottom-end">
            <Tooltip label={t('general.actions')}>
              <MenuButton
                as={IconButton}
                aria-label={t('general.actions')}
                icon={<Icons.DotsHorizontal boxSize={5} />}
                minW="24px"
                size="xs"
                variant="ghost"
              />
            </Tooltip>
            <Portal>
              <MenuList minW="190px">
                <MenuItem onClick={() => onEdit(user)}>
                  {t('general.edit')}
                </MenuItem>
                <MenuItem color="status.danger" onClick={() => onRemove(user)}>
                  {t('article.management.removeUser')}
                </MenuItem>
                {!user.inCustomerTeam && (
                  <MenuItem onClick={() => onAddToCustomerTeam(user)}>
                    {t('article.management.addToCustomerTeam')}
                  </MenuItem>
                )}
              </MenuList>
            </Portal>
          </Menu>
        }
        email={user.email}
        label={secondaryLabel}
        name={user.name}
        showTeams={true}
        size="sm"
      />
      {!user.inCustomerTeam && (
        <Badge
          alignSelf="flex-start"
          bg="status.dangerBackground"
          color="status.danger"
          fontSize="9px"
          letterSpacing="0.05em"
          textTransform="uppercase"
        >
          {t('article.management.notInCustomerTeam')}
        </Badge>
      )}
    </Flex>
  );
}

function userMatchesRole(user: ArticleUser, userType: UserType | null) {
  const normalizedUserType = user.userType
    ? ensureEnumNumber(UserType, user.userType)
    : null;

  if (userType === null) {
    return (
      normalizedUserType !== UserType.ProductDeveloper &&
      normalizedUserType !== UserType.CustomerService
    );
  }

  return normalizedUserType === userType;
}

function roleName(user: ArticleUser, t: TFunction) {
  return (
    getUserTypeLabel(user.userType, user.customUserType, t) ??
    t('article.management.teamMember')
  );
}
